import { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

import HeaderBar from "../../components/HeaderBar";
import PayRollLayoutWrapper from "../../components/PayRollLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { Filter, Search, Save, ArrowLeft } from "lucide-react";

const BASE_URL = "https://nissi-india.com"; // change if needed

const MONTHS = [
  { label: "Select", value: "Select" },
  { label: "January", value: "1" },
  { label: "February", value: "2" },
  { label: "March", value: "3" },
  { label: "April", value: "4" },
  { label: "May", value: "5" },
  { label: "June", value: "6" },
  { label: "July", value: "7" },
  { label: "August", value: "8" },
  { label: "September", value: "9" },
  { label: "October", value: "10" },
  { label: "November", value: "11" },
  { label: "December", value: "12" },
];

function monthLabelByValue(v) {
  const m = MONTHS.find((x) => String(x.value) === String(v));
  return m?.label || "";
}

export default function MonthlyAttendanceLockDownEntry() {
  const nav = useNavigate();
  const { theme } = useContext(ThemeContext);
  const skin = getCurrentSkin(theme);
  const isDark = skin?.mode === "dark";

  const cardBase = `rounded-3xl shadow-sm border ${isDark ? "bg-white/5 border-white/10" : "bg-white border-gray-200"}`;
  const labelBase = `text-sm font-extrabold ${isDark ? "text-gray-200" : "text-gray-700"}`;
  const inputBase = `px-4 py-3 rounded-2xl border outline-none font-bold ${
    isDark ? "bg-white/5 border-white/10 text-white placeholder:text-gray-400" : "bg-white border-gray-200 text-gray-900"
  }`;

  const primaryBtn = `inline-flex items-center gap-2 px-4 py-3 rounded-2xl font-extrabold ${
    isDark ? "bg-blue-600 text-white hover:bg-blue-500" : "bg-blue-600 text-white hover:bg-blue-700"
  } disabled:opacity-60`;

  const successBtn = `inline-flex items-center gap-2 px-4 py-3 rounded-2xl font-extrabold ${
    isDark ? "bg-emerald-600 text-white hover:bg-emerald-500" : "bg-emerald-600 text-white hover:bg-emerald-700"
  } disabled:opacity-60`;

  const ghostBtn = `inline-flex items-center gap-2 px-4 py-3 rounded-2xl font-extrabold ${
    isDark ? "bg-white/10 text-white hover:bg-white/15" : "bg-gray-100 text-gray-900 hover:bg-gray-200"
  }`;

  const finYearId = useMemo(() => Number(localStorage.getItem("finYearId") || 0), []);
  const loginUserId = useMemo(() => localStorage.getItem("userId") || localStorage.getItem("LoginUserId") || "", []);

  const [loading, setLoading] = useState(false);

  const [branches, setBranches] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [years, setYears] = useState(["Select"]);

  const [branchId, setBranchId] = useState("Select");
  const [monthValue, setMonthValue] = useState("Select");
  const [year, setYear] = useState("Select");
  const [departmentId, setDepartmentId] = useState("Select");

  const [fromDateText, setFromDateText] = useState("");
  const [toDateText, setToDateText] = useState("");

  // Add grid
  const [rows, setRows] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);

  // Edit mode
  const [MAID, setMAID] = useState(() => Number(localStorage.getItem("MAID") || 0));
  const [editRows, setEditRows] = useState([]);
  const [editSelected, setEditSelected] = useState([]);

  function axiosHeaders() {
    return {
      "x-loginuserid": loginUserId,
      "x-branchid": localStorage.getItem("branchId") || "",
      "x-branchtype": localStorage.getItem("branchType") || "",
    };
  }

 async function loadMeta() {
  try {
    setLoading(true);

    const url = `${BASE_URL}/api/payroll/monthly-attendance-lockdown/meta?finYearId=${finYearId}`;
    const res = await axios.get(url, { headers: axiosHeaders(), timeout: 60000 });
    const data = res?.data;

    if (!data?.success) {
      toast.error(data?.message || "Meta load failed");
      return;
    }

    // ---- branches: normalize + remove undefined rows ----
    const rawBranches = Array.isArray(data?.branches) ? data.branches : [];
    const cleanedBranches = rawBranches
      .map((b) => ({
        id: String(
          b?.fld_Branchid ??
            b?.fld_branchid ??
            b?.BranchId ??
            b?.branchId ??
            b?.id ??
            ""
        ),
        name: String(
          b?.fld_BranchName ??
            b?.fld_branchname ??
            b?.BranchName ??
            b?.branchName ??
            b?.name ??
            ""
        ),
      }))
      .filter((b) => b.id && b.name && b.id !== "0"); // removes undefined/blank options

    setBranches(cleanedBranches);

    // ---- departments: keep same, just make sure array ----
    setDepartments(Array.isArray(data?.departments) ? data.departments : []);

    // ---- years: make sure Select exists ----
    const rawYears = Array.isArray(data?.years) ? data.years : [];
    const yearsWithSelect =
      rawYears.length > 0
        ? [String(rawYears[0]) === "Select" ? null : "Select", ...rawYears].filter(Boolean)
        : ["Select"];
    setYears(yearsWithSelect);

    // ---- defaults (prefer localStorage branchId if exists) ----
    const loginBranchId = localStorage.getItem("branchId");

    const defaultBranchId = String(data?.defaultBranchId ?? loginBranchId ?? "Select");
    const validBranch =
      defaultBranchId !== "Select" && cleanedBranches.some((b) => b.id === defaultBranchId);

    setBranchId(validBranch ? defaultBranchId : "Select");

    // Month: keep whatever your UI expects (name or number) - use your meta response
    setMonthValue(String(data?.defaultMonth ?? "Select"));

    // Year:
    setYear(String(data?.defaultYear ?? "Select"));
  } catch (e) {
    toast.error(e?.response?.data?.message || e?.message || "Meta load failed");
  } finally {
    setLoading(false);
  }
}


  async function loadEditIfAny(maid) {
    if (!maid) return;
    try {
      setLoading(true);
      const url = `${BASE_URL}/api/payroll/monthly-attendance-lockdown/edit-init?finYearId=${finYearId}&MAID=${maid}`;
      const { data } = await axios.get(url, {
        headers: axiosHeaders(),
        timeout: 120000,
      });

      if (!data?.success) {
        toast.error(data?.message || "Edit init failed");
        return;
      }
      setEditRows(data.rows || []);
      setEditSelected([]);
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadMeta();
    // if MAID exists, load edit grid like ASP
    if (MAID) loadEditIfAny(MAID);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function toggleSelectAllAdd(checked) {
    if (checked) {
      setSelectedIds(rows.map((r, idx) => String(r.fld_Employeeid ?? r.fld_employeeid ?? idx)));
    } else {
      setSelectedIds([]);
    }
  }

  function toggleAddRow(empKey) {
    const k = String(empKey);
    setSelectedIds((prev) => (prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k]));
  }

  function toggleSelectAllEdit(checked) {
    if (checked) {
      setEditSelected(editRows.map((_, idx) => String(idx)));
    } else setEditSelected([]);
  }

  function toggleEditRow(idx) {
    const k = String(idx);
    setEditSelected((prev) => (prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k]));
  }

  async function searchGrid() {
    try {
      if (!finYearId) return toast.error("finYearId missing");
      if (branchId === "Select") return toast.error("Please select Branch..");
      if (monthValue === "Select") return toast.error("Please select Month..");
      if (year === "Select") return toast.error("Please select Year..");

      setLoading(true);
      setRows([]);
      setSelectedIds([]);

      const payload = {
        branchId,
        fromDateText,
        toDateText,
        monthValue,
        monthText: monthLabelByValue(monthValue),
        year,
        departmentId,
      };

      const url = `${BASE_URL}/api/payroll/monthly-attendance-lockdown/search?finYearId=${finYearId}`;
      const { data } = await axios.post(url, payload, {
        headers: axiosHeaders(),
        timeout: 180000, // IMPORTANT for heavy proc
      });

      if (!data?.success) {
        toast.error(data?.message || "Search failed");
        return;
      }

      // Normalize + keep remarks editable
      const list = (data.rows || []).map((r) => ({
        ...r,
        __remarks: r.fld_Remarks ?? r.remarks ?? "",
      }));

      setRows(list);
      toast.success(`Loaded ${list.length} records`);
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  }

  async function submitSelected() {
    try {
      if (selectedIds.length === 0) return toast.error("Please select any one");
      setLoading(true);

      const selectedRows = rows
        .filter((r, idx) => {
          const key = String(r.fld_Employeeid ?? r.fld_employeeid ?? idx);
          return selectedIds.includes(key);
        })
        .map((r) => ({
          ...r,
          // send remarks back
          fld_Remarks: r.__remarks,
        }));

      const url = `${BASE_URL}/api/payroll/monthly-attendance-lockdown/submit?finYearId=${finYearId}`;
      const { data } = await axios.post(
        url,
        {
          branchId,
          monthValue,
          monthText: monthLabelByValue(monthValue),
          year,
          rowsSelected: selectedRows,
        },
        {
          headers: axiosHeaders(),
          timeout: 180000,
        }
      );

      if (!data?.success) {
        toast.error(data?.message || "Submit failed");
        return;
      }

      toast.success(data.message || "Inserted Successfully..");
      // ASP redirects to listing; in React, you can navigate to listing route
      nav("/payroll/MonthlyAttendance");
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  }

  async function submitEditUpdates() {
    try {
      if (!MAID) return toast.error("MAID missing");
      if (editSelected.length === 0) return toast.error("Please select any one");
      setLoading(true);

      const updates = editSelected.map((k) => {
        const idx = Number(k);
        return editRows[idx];
      });

      const url = `${BASE_URL}/api/payroll/monthly-attendance-lockdown/edit-submit?finYearId=${finYearId}`;
      const { data } = await axios.put(
        url,
        {
          MAID,
          branchId,
          monthValue,
          year,
          updates: updates.map((r) => ({
            ...r,
            fld_Remarks: r.__remarks ?? r.fld_Remarks ?? "",
          })),
        },
        {
          headers: axiosHeaders(),
          timeout: 180000,
        }
      );

      if (!data?.success) {
        toast.error(data?.message || "Update failed");
        return;
      }

      toast.success(data.message || "Updated Successfully..");
      localStorage.removeItem("MAID");
      setMAID(0);
      setEditRows([]);
      setEditSelected([]);
      nav("/payroll/MonthlyAttendanceListing");
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  }

  const tableWrap = `w-full overflow-auto rounded-3xl border ${
    isDark ? "border-white/10" : "border-gray-200"
  }`;

  const thBase = `px-3 py-3 text-xs font-extrabold whitespace-nowrap ${
    isDark ? "text-gray-200 bg-white/5" : "text-gray-700 bg-gray-50"
  }`;

  const tdBase = `px-3 py-2 text-sm font-bold whitespace-nowrap ${
    isDark ? "text-gray-100 border-white/10" : "text-gray-800 border-gray-200"
  }`;

  return (
    <PayRollLayoutWrapper>
      <ToastContainer position="top-right" autoClose={2500} />
      <HeaderBar />

      <div className="p-4 lg:p-6">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 mb-4">
          <div>
            <div className={`text-2xl font-extrabold ${isDark ? "text-white" : "text-gray-900"}`}>
              Monthly Attendance (LockDown)
            </div>
            <div className={`text-sm font-bold ${isDark ? "text-gray-300" : "text-gray-600"}`}>
              INFRA SCREEN : C-23/1
            </div>
          </div>

          <button className={ghostBtn} onClick={() => nav(-1)}>
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
        </div>

        {/* Filters */}
        <div className={cardBase + " p-6 mb-6"}>
          <div className="flex items-center gap-2 mb-4">
            <Filter className={isDark ? "text-gray-200" : "text-gray-700"} />
            <div className="text-lg font-extrabold">Filters</div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-end">
            {/* Branch */}
           <div className="lg:col-span-4">
  <label className={labelBase}>Branch *</label>
  <select
    className={`${inputBase} w-full`}
    value={branchId}
    onChange={(e) => setBranchId(e.target.value)}
  >
    <option value="Select">Select</option>
    {branches.map((b) => (
      <option key={b.id} value={b.id}>
        {b.name}
      </option>
    ))}
  </select>
</div>


            {/* From Date */}
            <div className="lg:col-span-2">
              <label className={labelBase}>From Date</label>
              <input
                className={`${inputBase} w-full`}
                value={fromDateText}
                onChange={(e) => setFromDateText(e.target.value)}
                placeholder="dd/MM/yyyy"
              />
            </div>

            {/* To Date */}
            <div className="lg:col-span-2">
              <label className={labelBase}>To Date</label>
              <input
                className={`${inputBase} w-full`}
                value={toDateText}
                onChange={(e) => setToDateText(e.target.value)}
                placeholder="dd/MM/yyyy"
              />
            </div>

            {/* Year */}
            <div className="lg:col-span-2">
              <label className={labelBase}>Year *</label>
              <select className={`${inputBase} w-full`} value={year} onChange={(e) => setYear(e.target.value)}>
                {years.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </div>

            {/* Month */}
            <div className="lg:col-span-2">
              <label className={labelBase}>Month *</label>
              <select className={`${inputBase} w-full`} value={monthValue} onChange={(e) => setMonthValue(e.target.value)}>
                {MONTHS.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Department */}
            <div className="lg:col-span-4">
              <label className={labelBase}>Department</label>
              <select
                className={`${inputBase} w-full`}
                value={departmentId}
                onChange={(e) => setDepartmentId(e.target.value)}
              >
                <option value="Select">Select</option>
                {departments.map((d, i) => {
                  const val = String(d.fld_deptid ?? d.deptid ?? d.Value ?? d.value ?? "");
                  const txt = String(d.fld_departmentname ?? d.departmentname ?? d.Text ?? d.text ?? val);
                  return (
                    <option key={val || i} value={val}>
                      {txt}
                    </option>
                  );
                })}
              </select>
            </div>

            {/* Actions */}
            <div className="lg:col-span-8 lg:justify-self-end">
              <div className="flex flex-wrap lg:flex-nowrap gap-3 justify-start lg:justify-end">
                <button onClick={searchGrid} className={primaryBtn} disabled={loading}>
                  <Search className="w-4 h-4" />
                  Search
                </button>

                {!MAID ? (
                  <button onClick={submitSelected} className={successBtn} disabled={loading || selectedIds.length === 0}>
                    <Save className="w-4 h-4" />
                    Submit Selected
                  </button>
                ) : (
                  <button onClick={submitEditUpdates} className={successBtn} disabled={loading || editSelected.length === 0}>
                    <Save className="w-4 h-4" />
                    Update Selected
                  </button>
                )}

                <div
                  className={`px-4 py-3 rounded-2xl text-sm font-extrabold whitespace-nowrap ${
                    isDark ? "bg-white/10 text-white" : "bg-gray-100 text-gray-800"
                  }`}
                >
                  Selected: {MAID ? editSelected.length : selectedIds.length}
                </div>
              </div>
            </div>
          </div>

          <div className="mt-3 text-sm opacity-70 font-bold">
            Tip: Select checkbox → edit Remarks → click Submit/Update.
          </div>
        </div>

        {/* ADD Grid */}
        {!MAID && (
          <div className={cardBase + " p-0"}>
            <div className={tableWrap}>
              <table className="min-w-[1200px] w-full border-collapse">
                <thead>
                  <tr>
                    <th className={thBase}>
                      <input
                        type="checkbox"
                        checked={rows.length > 0 && selectedIds.length === rows.length}
                        onChange={(e) => toggleSelectAllAdd(e.target.checked)}
                      />{" "}
                      Select All
                    </th>
                    <th className={thBase}>S.No</th>
                    <th className={thBase}>Employee Name</th>
                    <th className={thBase}>Employee Code</th>
                    <th className={thBase}>Designation</th>
                    <th className={thBase}>Opening Leave Balance</th>
                    <th className={thBase}>Leave Taken</th>
                    <th className={thBase}>LOP</th>
                    <th className={thBase}>Compulsory LOP</th>
                    <th className={thBase}>Present</th>
                    <th className={thBase}>Manage Leave</th>
                    <th className={thBase}>Manage Leave 100%</th>
                    <th className={thBase}>Total Days</th>
                    <th className={thBase}>Salary Days</th>
                    <th className={thBase}>Days Worked</th>
                    <th className={thBase}>Holiday</th>
                    <th className={thBase}>Remarks</th>
                  </tr>
                </thead>

                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td className={`${tdBase} text-center`} colSpan={17}>
                        No Records Found
                      </td>
                    </tr>
                  ) : (
                    rows.map((r, idx) => {
                      const empKey = String(r.fld_Employeeid ?? r.fld_employeeid ?? idx);
                      const checked = selectedIds.includes(empKey);

                      return (
                        <tr key={empKey} className={`${isDark ? "border-white/10" : "border-gray-200"} border-t`}>
                          <td className={tdBase}>
                            <input type="checkbox" checked={checked} onChange={() => toggleAddRow(empKey)} />
                          </td>
                          <td className={tdBase}>{idx + 1}</td>
                          <td className={`${tdBase} whitespace-normal`}>{r.fld_Employee ?? "-"}</td>
                          <td className={tdBase}>{r.fld_Employeecode ?? "-"}</td>
                          <td className={`${tdBase} whitespace-normal`}>{r.fld_designationname ?? "-"}</td>
                          <td className={tdBase}>{r.fld_totleaveob ?? "0"}</td>
                          <td className={tdBase}>{r.leave ?? r.fld_Leave ?? "0"}</td>
                          <td className={tdBase}>{r.fld_lop ?? "0"}</td>
                          <td className={tdBase}>{r.fld_Compulsorylop ?? "0"}</td>
                          <td className={tdBase}>{r.present ?? "0"}</td>
                          <td className={tdBase}>{r.holidays ?? "0"}</td>
                          <td className={tdBase}>{r.fld_manageleave100p ?? "0"}</td>
                          <td className={tdBase}>{r.totalDays ?? "0"}</td>
                          <td className={tdBase}>{r.salarydays ?? "0"}</td>
                          <td className={tdBase}>{r.daysworked ?? "0"}</td>
                          <td className={tdBase}>{r.Hholidays ?? "0"}</td>
                          <td className={`${tdBase} whitespace-normal`}>
                            <textarea
                              rows={2}
                              className={`${inputBase} w-[320px]`}
                              value={r.__remarks}
                              onChange={(e) => {
                                const v = e.target.value;
                                setRows((prev) => prev.map((x, j) => (j === idx ? { ...x, __remarks: v } : x)));
                              }}
                            />
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* EDIT Grid */}
        {MAID && (
          <div className={cardBase + " p-0"}>
            <div className="p-4 font-extrabold">
              Edit Mode (MAID: {MAID}) — this matches ASP when Session["MAID"] exists.
            </div>

            <div className={tableWrap}>
              <table className="min-w-[1200px] w-full border-collapse">
                <thead>
                  <tr>
                    <th className={thBase}>
                      <input
                        type="checkbox"
                        checked={editRows.length > 0 && editSelected.length === editRows.length}
                        onChange={(e) => toggleSelectAllEdit(e.target.checked)}
                      />{" "}
                      Select All
                    </th>
                    <th className={thBase}>S.No</th>
                    <th className={thBase}>Employee Name</th>
                    <th className={thBase}>Employee Code</th>
                    <th className={thBase}>Designation</th>
                    <th className={thBase}>Days Worked</th>
                    <th className={thBase}>Leave Taken</th>
                    <th className={thBase}>Manage Leave</th>
                    <th className={thBase}>Manage Leave 100%</th>
                    <th className={thBase}>Holiday</th>
                    <th className={thBase}>Absence</th>
                    <th className={thBase}>OT</th>
                    <th className={thBase}>Remarks</th>
                  </tr>
                </thead>

                <tbody>
                  {editRows.length === 0 ? (
                    <tr>
                      <td className={`${tdBase} text-center`} colSpan={13}>
                        No Records Found
                      </td>
                    </tr>
                  ) : (
                    editRows.map((r, idx) => {
                      const checked = editSelected.includes(String(idx));
                      return (
                        <tr key={idx} className={`${isDark ? "border-white/10" : "border-gray-200"} border-t`}>
                          <td className={tdBase}>
                            <input type="checkbox" checked={checked} onChange={() => toggleEditRow(idx)} />
                          </td>
                          <td className={tdBase}>{idx + 1}</td>
                          <td className={`${tdBase} whitespace-normal`}>{r.fld_Employee ?? "-"}</td>
                          <td className={tdBase}>{r.fld_Employeecode ?? "-"}</td>
                          <td className={`${tdBase} whitespace-normal`}>{r.fld_designationname ?? "-"}</td>

                          {/* editable fields like ASP edit grid */}
                          {[
                            ["fld_Daysworked", "Days Worked"],
                            ["fld_Leave", "Leave Taken"],
                            ["fld_ManageLeave", "Manage Leave"],
                            ["fld_ManageLeave100p", "Manage Leave 100%"],
                            ["fld_holiday", "Holiday"],
                            ["fld_Absence", "Absence"],
                            ["fld_OT", "OT"],
                          ].map(([key]) => (
                            <td key={key} className={tdBase}>
                              <input
                                className={`${inputBase} w-[110px]`}
                                value={r[key] ?? "0"}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  setEditRows((prev) => prev.map((x, j) => (j === idx ? { ...x, [key]: v } : x)));
                                }}
                              />
                            </td>
                          ))}

                          <td className={`${tdBase} whitespace-normal`}>
                            <textarea
                              rows={2}
                              className={`${inputBase} w-[320px]`}
                              value={r.__remarks ?? r.fld_Remarks ?? ""}
                              onChange={(e) => {
                                const v = e.target.value;
                                setEditRows((prev) => prev.map((x, j) => (j === idx ? { ...x, __remarks: v } : x)));
                              }}
                            />
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            <div className="p-4">
              <button
                className={ghostBtn}
                onClick={() => {
                  localStorage.removeItem("MAID");
                  setMAID(0);
                  setEditRows([]);
                  setEditSelected([]);
                }}
              >
                Exit Edit Mode
              </button>
            </div>
          </div>
        )}
      </div>
    </PayRollLayoutWrapper>
  );
}
